/**
 * CrewTable Component
 *
 * Displays crew members in a table format with all details visible.
 *
 * Two deliberate absences: there is no Deactivate action and no Archive
 * action on a row. Both are per-org state changes that deserve the context of
 * the person's profile — how long since they logged in, what they are still
 * assigned to — so both live there. The list offers ARCHIVING IN BULK only,
 * via the selection checkboxes, because archiving is reversible and takes
 * nothing away. Deactivation has no bulk form at all.
 */

import { Link } from 'react-router-dom';
import type { User, CrewSortBy, SortDir } from '../../api/users';
import { TableRowSkeleton } from '../ui';
import { ROLE_CONFIG, MEMBER_STATE, USER_STATUS } from '../../config/statusStyles';
import { formatDate as formatDateUtil } from '../../utils/dateFormatting';

interface CrewTableProps {
  users: User[];
  loading: boolean;
  /** Selected ids for the bulk action bar. Omit to hide selection entirely. */
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  sortBy?: CrewSortBy;
  sortDir?: SortDir;
  onSort?: (column: CrewSortBy) => void;
  /** Viewer's own id — never selectable, since you cannot archive yourself. */
  currentUserId?: string;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return 'Never';
  return formatDateUtil(dateString);
};

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

/**
 * A sortable column header. The arrow appears only on the active column: a
 * neutral glyph on every header reads as decoration and stops meaning
 * anything.
 */
function SortableHeader({
  column,
  label,
  sortBy,
  sortDir,
  onSort,
}: {
  column: CrewSortBy;
  label: string;
  sortBy?: CrewSortBy;
  sortDir?: SortDir;
  onSort?: (column: CrewSortBy) => void;
}) {
  const active = sortBy === column;

  if (!onSort) {
    return <th className={TH}>{label}</th>;
  }

  return (
    <th className={TH} aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group inline-flex items-center gap-1 uppercase tracking-wider focus-ring"
      >
        {label}
        <span className={active ? 'text-ink' : 'text-transparent group-hover:text-ink-muted'}>
          {active && sortDir === 'asc' ? '↑' : '↓'}
        </span>
      </button>
    </th>
  );
}

export default function CrewTable({
  users,
  loading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  sortBy,
  sortDir,
  onSort,
  currentUserId,
}: CrewTableProps) {
  const selectable = !!selectedIds && !!onToggleSelect;
  const columnCount = selectable ? 8 : 7;

  // "Select all" covers the rows you could actually act on: your own row is
  // never selectable, so counting it would leave the box permanently
  // indeterminate on a one-person list.
  const selectableUsers = users.filter((u) => u.id !== currentUserId);
  const allSelected =
    selectableUsers.length > 0 && selectableUsers.every((u) => selectedIds?.has(u.id));

  const headers = (
    <thead className="bg-gray-50">
      <tr>
        {selectable && (
          <th className="px-4 py-3 w-12">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleSelectAll}
              disabled={selectableUsers.length === 0}
              aria-label="Select all crew on this page"
              className="h-4 w-4 border-hairline text-rack focus:ring-rack"
            />
          </th>
        )}
        <SortableHeader column="name" label="Name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        <th className={TH}>Contact</th>
        <th className={TH}>Role</th>
        <th className={TH}>Status</th>
        <SortableHeader
          column="lastLogin"
          label="Last Login"
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
        />
        <th className={TH}>Assignments</th>
        <th className={`${TH} text-right`}>Actions</th>
      </tr>
    </thead>
  );

  if (loading) {
    return (
      <div className="bg-white border border-hairline overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          {headers}
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(6)].map((_, i) => (
              <TableRowSkeleton key={i} columns={columnCount} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="bg-white border border-hairline overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {headers}
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => {
              const roleInfo = ROLE_CONFIG[user.role];
              const state = user.memberState ?? 'ACTIVE';
              const stateInfo = MEMBER_STATE[state];
              const isSelf = user.id === currentUserId;
              const selected = selectedIds?.has(user.id) ?? false;

              return (
                <tr
                  key={user.id}
                  className={`hover:bg-gray-50 ${selected ? 'bg-paper' : ''} ${
                    // Archived rows read as filed away rather than broken.
                    state === 'ARCHIVED' ? 'text-ink-secondary' : ''
                  }`}
                >
                  {selectable && (
                    <td className="px-4 py-4 w-12">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onToggleSelect?.(user.id)}
                        disabled={isSelf}
                        aria-label={`Select ${user.firstName} ${user.lastName}`}
                        title={isSelf ? 'You cannot archive yourself' : undefined}
                        className="h-4 w-4 border-hairline text-rack focus:ring-rack disabled:opacity-40"
                      />
                    </td>
                  )}

                  {/* Name with avatar */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.profilePhoto ? (
                        <img
                          src={user.profilePhoto}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                          {getInitials(user.firstName, user.lastName)}
                        </div>
                      )}
                      <div className="ml-4">
                        <Link
                          to={`/admin/crew/${user.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-primary-600"
                        >
                          {user.firstName} {user.lastName}
                        </Link>
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    {user.phone && (
                      <div className="text-sm text-gray-500">{user.phone}</div>
                    )}
                  </td>

                  {/* Role */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-0.5 text-xs font-medium ${roleInfo.className}`}>
                      {roleInfo.label}
                    </span>
                  </td>

                  {/* Status. Membership state first, because archived and
                      deactivated are what an admin is looking for here; a
                      never-logged-in active member still shows as Pending. */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {state !== 'ACTIVE' ? (
                      <span
                        className={`px-2.5 py-0.5 text-xs font-medium ${stateInfo.className}`}
                        title={
                          user.archivedReason === 'AUTO_NO_RESPONSE'
                            ? 'Archived automatically after no response to recent events'
                            : undefined
                        }
                      >
                        {stateInfo.label}
                        {user.archivedReason === 'AUTO_NO_RESPONSE' && ' (auto)'}
                      </span>
                    ) : !user.lastLoginAt ? (
                      <span className={`px-2.5 py-0.5 text-xs font-medium ${USER_STATUS.PENDING.className}`}>
                        {USER_STATUS.PENDING.label}
                      </span>
                    ) : (
                      <span className={`px-2.5 py-0.5 text-xs font-medium ${MEMBER_STATE.ACTIVE.className}`}>
                        Active
                      </span>
                    )}
                  </td>

                  {/* Last Login */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.lastLoginAt)}
                  </td>

                  {/* Assignments */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.assignmentCount ?? 0}
                  </td>

                  {/* Actions — navigation only. Archive and deactivate are
                      profile decisions; see this file's header. No "View"
                      link: the name is already one, and with the selection
                      column added the extra link pushed the table wider than
                      a laptop screen for no new capability. */}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link
                      to={`/admin/crew/${user.id}/edit`}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
